import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  BudgetCollaborationService,
  BudgetEvidence,
  BudgetLineKind
} from '../../../services/budget-collaboration.service';
import {
  ServiceOrderWorkSectionItem,
  ServiceOrderWorkSectionService
} from '../../../services/service-order-work-sections.service';
import { ErrorService } from '../../../shared/services/error.service';
import { isAttachmentImage } from '../../../shared/utils/attachment-image.util';

interface BudgetLineView {
  kind: BudgetLineKind;
  id: number;
  index: number;
  label: string;
  description: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  maintenanceKind: 'Preventiva' | 'Corretiva';
  chargePercent: number | null;
  approvalStatus: string;
  rejectionReason?: string | null;
}

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

@Component({
  selector: 'app-budget-detail-items',
  templateUrl: './budget-detail-items.component.html',
  styleUrls: ['./budget-collaboration.component.css']
})
export class BudgetDetailItemsComponent implements OnChanges {
  @Input() budgetId: number | null = null;
  @Input() services: ServiceOrderWorkSectionService[] = [];
  @Input() items: ServiceOrderWorkSectionItem[] = [];
  @Input() executionType: 'Base' | 'Parceiro' = 'Base';
  @Input() providerName: string | null = null;

  lines: BudgetLineView[] = [];
  evidencias: BudgetEvidence[] = [];
  loadingEvidences = false;
  uploadingLineKey: string | null = null;
  expandedLineKey: string | null = null;

  constructor(
    private collaborationService: BudgetCollaborationService,
    private errorService: ErrorService
  ) {}

  /** Exibe o "% a mais" (acréscimo) no detalhe do orçamento. */
  get canViewChargePercent(): boolean {
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['services'] || changes['items']) {
      this.rebuildLines();
    }
    if (changes['budgetId']) {
      this.evidencias = [];
      this.loadEvidences();
    }
  }

  get hasLines(): boolean {
    return this.lines.length > 0;
  }

  get generalEvidencias(): BudgetEvidence[] {
    return this.evidencias.filter(e => !e.linhaId);
  }

  executionLabel(): string {
    if (this.executionType === 'Parceiro') {
      return this.providerName ? `Parceiro — ${this.providerName}` : 'Oficina parceira';
    }
    return 'Base — oficina própria';
  }

  toggleLine(key: string): void {
    this.expandedLineKey = this.expandedLineKey === key ? null : key;
  }

  isExpanded(key: string): boolean {
    return this.expandedLineKey === key;
  }

  lineKey(line: BudgetLineView): string {
    return `${line.kind}-${line.id}`;
  }

  evidencesFor(line: BudgetLineView): BudgetEvidence[] {
    return this.evidencias.filter(
      e => e.linhaId === line.id && (e.linhaTipo || 'servico') === line.kind
    );
  }

  approvalClass(status: string): string {
    const s = (status || 'Pendente').toLowerCase();
    if (s.includes('aprov')) return 'line-status--approved';
    if (s.includes('recus') || s.includes('reprov')) return 'line-status--rejected';
    return 'line-status--pending';
  }

  formatCurrency(value: number): string {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatPercent(value: number | null | undefined): string {
    if (value == null || Number(value) <= 0) return '—';
    return `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
  }

  /** Exibe o acréscimo na linha compacta (sempre com sinal). */
  formatChargeBump(value: number | null | undefined): string {
    const n = Number(value || 0);
    return `+${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
  }

  isPreventiva(line: BudgetLineView): boolean {
    return line.maintenanceKind === 'Preventiva';
  }

  isImage(e: BudgetEvidence): boolean {
    return isAttachmentImage(e);
  }

  onLineFileSelected(event: Event, line: BudgetLineView): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.budgetId) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.errorService.showWarningMessage('Arquivo excede o limite de 25 MB.');
      return;
    }

    const key = this.lineKey(line);
    this.uploadingLineKey = key;

    this.collaborationService.uploadAndRegisterEvidence(this.budgetId, file, {
      linhaTipo: line.kind,
      linhaId: line.id
    }).subscribe({
      next: nova => {
        this.evidencias = [...this.evidencias, nova];
        this.uploadingLineKey = null;
        this.expandedLineKey = key;
        this.errorService.showSuccessMessage('Evidência anexada ao item.');
      },
      error: err => {
        this.uploadingLineKey = null;
        this.errorService.showErrorMessage(err?.message || 'Falha ao enviar o arquivo.');
      }
    });
  }

  private rebuildLines(): void {
    const serviceLines = (this.services || []).map((s, i) => this.mapServiceLine(s, i));
    const itemLines = (this.items || []).map((it, i) => this.mapItemLine(it, i));
    this.lines = [...serviceLines, ...itemLines];
  }

  private mapServiceLine(s: ServiceOrderWorkSectionService, index: number): BudgetLineView {
    return {
      kind: 'servico',
      id: s.id,
      index: index + 1,
      label: `Serviço ${index + 1}`,
      description: s.description,
      quantity: s.quantity,
      unitValue: s.unitValue,
      totalValue: s.totalValue,
      maintenanceKind: this.normalizeKind(s.maintenanceKind),
      chargePercent: s.chargePercent ?? null,
      approvalStatus: s.approvalStatus || 'Pendente',
      rejectionReason: s.rejectionReason
    };
  }

  private mapItemLine(it: ServiceOrderWorkSectionItem, index: number): BudgetLineView {
    return {
      kind: 'item',
      id: it.id,
      index: index + 1,
      label: `Peça ${index + 1}`,
      description: it.description,
      quantity: it.quantity,
      unitValue: it.unitValue,
      totalValue: it.totalValue,
      maintenanceKind: this.normalizeKind(it.maintenanceKind),
      chargePercent: it.chargePercent ?? null,
      approvalStatus: it.approvalStatus || 'Pendente',
      rejectionReason: it.rejectionReason
    };
  }

  private normalizeKind(value: string | null | undefined): 'Preventiva' | 'Corretiva' {
    return String(value || '').toLowerCase().startsWith('prevent') ? 'Preventiva' : 'Corretiva';
  }

  private loadEvidences(): void {
    if (!this.budgetId) return;

    this.loadingEvidences = true;
    this.collaborationService.listEvidences(this.budgetId).subscribe({
      next: res => {
        this.evidencias = res ?? [];
        this.loadingEvidences = false;
      },
      error: () => {
        this.evidencias = [];
        this.loadingEvidences = false;
      }
    });
  }
}
